import { useEffect, useMemo, useState } from 'react';
import { PageHeader } from '@/deploy/components/layout/PageHeader';
import { Icon } from '@/deploy/components/ui/Icon';
import { Badge } from '@/deploy/components/ui/Badge';
import { Avatar } from '@/deploy/components/ui/Avatar';
import { Button } from '@/deploy/components/ui/Button';
import { Modal } from '@/deploy/components/ui/Modal';
import { Toggle } from '@/deploy/components/ui/Toggle';
import { PermissionLock } from '@/deploy/components/ui/PermissionLock';
import { GroupBadge } from '@/deploy/components/ui/GroupBadge';
import { ProjectGroupsModal } from '@/deploy/components/deploy/ProjectGroupsModal';
import { useSessionStore } from '@/deploy/stores/session.store';
import { projectsService } from '@/deploy/services/projects.service';
import type { Permission, ProjectGroup, Role } from '@/deploy/types';
import { can } from '@/deploy/utils/permissions';
import { gameGroupsEnabled, instanceErrorMessage } from '@/deploy/utils/apiInstances';
import { NetworkSettingsSection } from '@/deploy/components/network/settings/NetworkSettingsSection';
import styles from './Settings.module.scss';

// ============================================================
// Permissions groupées par catégorie pour l'affichage
// ============================================================
const PERMISSION_GROUPS: { label: string; items: { key: Permission; label: string; hint?: string }[] }[] = [
  {
    label: 'Observation',
    items: [
      { key: 'cicd.view', label: 'Voir le panel', hint: 'Requis pour accéder à l\'interface' },
      { key: 'cicd.logs', label: 'Voir les logs' },
    ],
  },
  {
    label: 'Déploiement',
    items: [
      { key: 'cicd.deploy.beta',    label: 'Déployer vers Bêta' },
      { key: 'cicd.deploy.preprod', label: 'Déployer vers Pré-prod' },
      { key: 'cicd.deploy.prod',    label: 'Déployer vers Prod',   hint: 'Très sensible — promotion 1 clic' },
    ],
  },
  {
    label: 'Exécution',
    items: [
      { key: 'cicd.restart',  label: 'Redémarrer les services' },
      { key: 'cicd.schedule', label: 'Planifier une action' },
    ],
  },
  {
    label: 'Configuration',
    items: [
      { key: 'cicd.config.dotnet',          label: 'Modifier la version .NET', hint: 'Global au projet' },
      { key: 'cicd.resourcepack.release',   label: 'Activer une release RP' },
    ],
  },
  {
    label: 'Réseau',
    items: [
      { key: 'cicd.network', label: 'Gérer domaines et certificats', hint: 'Du projet, dans ses domaines principaux' },
    ],
  },
];

const ROLE_OPTIONS: { value: Role; label: string; description: string }[] = [
  { value: 'admin',           label: 'Admin',            description: 'Accès complet · peut tout gérer' },
  { value: 'project-manager', label: 'Gestionnaire',     description: 'Pilote un projet · déploie en Bêta/Pré-prod' },
  { value: 'developer',       label: 'Développeur',      description: 'Crée des envs de dev · déploie en Bêta' },
  { value: 'viewer',          label: 'Invité',           description: 'Lecture seule' },
];

const AVATAR_COLORS = ['#3B82F6', '#A78BFA', '#38BDF8', '#10B981', '#F59E0B', '#EF4444', '#EC4899', '#6366F1'];

// ============================================================
// Page
// ============================================================
export function Settings() {
  const user = useSessionStore((s) => s.user);
  const users = useSessionStore((s) => s.users);
  const projects = useSessionStore((s) => s.projects);
  const currentProject = useSessionStore((s) => s.currentProject());
  const setProject = useSessionStore((s) => s.setProject);

  const [dotnetOpen, setDotnetOpen] = useState(false);
  const [cdnOpen, setCdnOpen] = useState(false);
  const [pteroOpen, setPteroOpen] = useState(false);
  const [pteroApiOpen, setPteroApiOpen] = useState(false);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [gameGroups, setGameGroups] = useState<ProjectGroup[] | null>(null);
  const [groupsError, setGroupsError] = useState<string | null>(null);
  const [procedureOpen, setProcedureOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [editProjectId, setEditProjectId] = useState<string | null>(null);
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [deleteProjectTarget, setDeleteProjectTarget] = useState<string | null>(null);
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [deleteUserTarget, setDeleteUserTarget] = useState<string | null>(null);

  const canAdmin = user.role === 'admin';
  const canDotnet = can(user, 'cicd.config.dotnet');

  // Groupes de jeu du projet actif, NationsGlory seulement (drapeau du CICD, absent = masqué).
  const groupsEnabled = gameGroupsEnabled(currentProject);
  useEffect(() => {
    if (!currentProject.id || !groupsEnabled) return;
    let alive = true;
    setGameGroups(null);
    setGroupsError(null);
    projectsService.getGroups(currentProject.id)
      .then((g) => { if (alive) setGameGroups(g); })
      .catch((e) => { if (alive) setGroupsError(instanceErrorMessage(e)); });
    return () => { alive = false; };
  }, [currentProject.id, groupsEnabled]);

  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow="Paramètres"
        title="Configuration"
        subtitle="Gestion des projets, des utilisateurs et des permissions"
      />

      {/* -------- Projets -------- */}
      <section className={styles.section}>
        <header className={styles.sectionHeader}>
          <div>
            <h2><Icon name="layers" size={16} /> Projets <Badge tone="muted">{projects.length}</Badge></h2>
            <p>Un projet regroupe ses containers MC, APIs, resource packs et envs.</p>
          </div>
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Icon name="plus" size={12} />}
            onClick={() => setCreateProjectOpen(true)}
            disabled={!canAdmin}
            title={!canAdmin ? 'Admin uniquement' : 'Nouveau projet'}
          >
            Nouveau projet
            {!canAdmin && <PermissionLock inline />}
          </Button>
        </header>
        <div className={styles.projectGrid}>
          {projects.map((p) => (
            <article
              key={p.id}
              className={[styles.projectCard, p.id === currentProject.id && styles.projectCardActive].filter(Boolean).join(' ')}
            >
              <header className={styles.projectCardHead}>
                <span className={styles.projectChip} style={{ background: p.color }}>{p.code}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className={styles.projectName}>{p.name}</div>
                  <div className={styles.projectDesc}>{p.description}</div>
                </div>
                {p.id === currentProject.id && <Badge tone="primary" dot>Actif</Badge>}
              </header>
              <div className={styles.projectMeta}>
                <div><span>.NET</span><code>{p.dotnetVersion}</code></div>
                <div><span>Créé</span><code>{new Date(p.createdAt).toLocaleDateString('fr-FR')}</code></div>
              </div>
              <footer className={styles.projectFooter}>
                {p.id !== currentProject.id && (
                  <Button variant="ghost" size="sm" onClick={() => setProject(p.id)}>
                    Activer
                  </Button>
                )}
                <Button variant="ghost" size="sm" leftIcon={<Icon name="settings" size={12} />} onClick={() => setEditProjectId(p.id)} disabled={!canAdmin}>
                  Gérer
                </Button>
                {projects.length > 1 && (
                  <Button variant="ghost" size="sm" leftIcon={<Icon name="x" size={12} />} onClick={() => setDeleteProjectTarget(p.id)} disabled={!canAdmin}>
                    Supprimer
                  </Button>
                )}
              </footer>
            </article>
          ))}
        </div>
      </section>

      {/* -------- Réseau : commun à tous les projets (admin) -------- */}
      {canAdmin && <NetworkSettingsSection />}

      {/* -------- Configuration avancée du projet actif -------- */}
      <section className={styles.section}>
        <header className={styles.sectionHeader}>
          <div>
            <h2>
              <Icon name="sparkles" size={16} /> Configuration avancée
              <Badge tone="warning">Sensible · {currentProject.name}</Badge>
            </h2>
            <p>Modifications globales au projet actif.</p>
          </div>
        </header>
        <div className={styles.card}>
          <div className={styles.advancedRow}>
            <div>
              <div className={styles.advancedLabel}>
                <Icon name="sparkles" size={14} /> Version .NET du runtime
              </div>
              <div className={styles.advancedHint}>
                Appliquée à tous les containers au prochain rebuild. <strong>Global au projet.</strong>
              </div>
            </div>
            <div className={styles.advancedValue}>
              <code className={styles.mono}>.NET {currentProject.dotnetVersion}</code>
              {canDotnet ? (
                <Button variant="secondary" size="sm" onClick={() => setDotnetOpen(true)}>
                  Modifier…
                </Button>
              ) : (
                <PermissionLock label="Admin" tooltip="Permission cicd.config.dotnet requise" />
              )}
            </div>
          </div>

          <div className={styles.advancedRow}>
            <div>
              <div className={styles.advancedLabel}>
                <Icon name="deploy" size={14} /> Configuration CDN
              </div>
              <div className={styles.advancedHint}>
                Cible SFTP + SSH cache-clear pour le déploiement des resource packs. <strong>Par env (dev/beta/preprod/prod).</strong>
              </div>
            </div>
            <div className={styles.advancedValue}>
              {canDotnet ? (
                <Button variant="secondary" size="sm" onClick={() => setCdnOpen(true)}>
                  Configurer…
                </Button>
              ) : (
                <PermissionLock label="Admin" tooltip="Permission cicd.config.dotnet requise" />
              )}
            </div>
          </div>

          <div className={styles.advancedRow}>
            <div>
              <div className={styles.advancedLabel}>
                <Icon name="server" size={14} /> Configuration Pterodactyl (containers)
              </div>
              <div className={styles.advancedHint}>
                URL panel + clés API (PTLA admin, PTLC client) pour les <strong>Minecraft containers</strong>. Par env preprod/prod uniquement.
              </div>
            </div>
            <div className={styles.advancedValue}>
              {canDotnet ? (
                <Button variant="secondary" size="sm" onClick={() => setPteroOpen(true)}>
                  Configurer…
                </Button>
              ) : (
                <PermissionLock label="Admin" tooltip="Permission cicd.config.dotnet requise" />
              )}
            </div>
          </div>

          <div className={styles.advancedRow}>
            <div>
              <div className={styles.advancedLabel}>
                <Icon name="zap" size={14} /> Configuration Pterodactyl (APIs)
              </div>
              <div className={styles.advancedHint}>
                Instance Ptero dédiée aux services API. <strong>4 envs</strong> : dev / beta / preprod / prod. Support DB MySQL pour bypass eggs read-only (comme containers).
              </div>
            </div>
            <div className={styles.advancedValue}>
              {canDotnet ? (
                <Button variant="secondary" size="sm" onClick={() => setPteroApiOpen(true)}>
                  Configurer…
                </Button>
              ) : (
                <PermissionLock label="Admin" tooltip="Permission cicd.config.dotnet requise" />
              )}
            </div>
          </div>

          {groupsEnabled && (
            <div className={styles.advancedRow}>
              <div>
                <div className={styles.advancedLabel}>
                  <Icon name="layers" size={14} /> Groupes de jeu
                </div>
                <div className={styles.advancedHint}>
                  Sous-ensembles de serveurs de jeu, chacun déclaré par la variable Pterodactyl RECUBE_GROUP (ex. Nova, Iris).
                  La page APIs règle ensuite, par environnement, quelle instance de chaque API sert chaque groupe, ou aucune
                  (Répartition). Sans répartition, tous les groupes reçoivent les instances Principales.
                </div>
              </div>
              <div className={styles.advancedValue}>
                {groupsError ? (
                  <span className={styles.muted} title={groupsError}>Indisponible</span>
                ) : gameGroups === null ? (
                  <span className={styles.muted}>…</span>
                ) : gameGroups.length === 0 ? (
                  <span className={styles.muted}>Aucun</span>
                ) : (
                  <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                    {gameGroups.map((g) => <GroupBadge key={g.key} group={g} size="md" />)}
                  </span>
                )}
                {canDotnet ? (
                  <Button variant="secondary" size="sm" onClick={() => setGroupsOpen(true)} disabled={!!groupsError}
                    title={groupsError ?? undefined}>
                    Configurer…
                  </Button>
                ) : (
                  <PermissionLock label="Admin" tooltip="Permission cicd.config.dotnet requise" />
                )}
              </div>
            </div>
          )}

          <div className={styles.advancedRow}>
            <div>
              <div className={styles.advancedLabel}>
                <Icon name="rocket" size={14} /> Procédure de mise à jour
              </div>
              <div className={styles.advancedHint}>
                Séquence d'étapes exécutée lors d'une promotion (whitelist on/off, stop/start, update images, push CDN…). <strong>Configurable par env preprod/prod.</strong>
              </div>
            </div>
            <div className={styles.advancedValue}>
              {canDotnet ? (
                <Button variant="secondary" size="sm" onClick={() => setProcedureOpen(true)}>
                  Éditer…
                </Button>
              ) : (
                <PermissionLock label="Admin" tooltip="Permission cicd.config.dotnet requise" />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* -------- Utilisateurs -------- */}
      <section className={styles.section}>
        <header className={styles.sectionHeader}>
          <div>
            <h2><Icon name="user" size={16} /> Utilisateurs & permissions <Badge tone="muted">{users.length}</Badge></h2>
            <p>Gestion des comptes, des rôles et des permissions granulaires.</p>
          </div>
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Icon name="plus" size={12} />}
            onClick={() => setCreateUserOpen(true)}
            disabled={!canAdmin}
          >
            Nouvel utilisateur
            {!canAdmin && <PermissionLock inline />}
          </Button>
        </header>
        <div className={styles.userList}>
          {users.map((u) => (
            <article key={u.id} className={[styles.userRow, u.id === user.id && styles.userRowSelf].filter(Boolean).join(' ')}>
              <Avatar name={u.name} color={u.avatarColor} size={40} />
              <div className={styles.userInfo}>
                <div className={styles.userName}>
                  {u.name}
                  {u.id === user.id && <Badge tone="primary">vous</Badge>}
                </div>
                <div className={styles.userEmail}>{u.email}</div>
              </div>
              <Badge tone={u.role === 'admin' ? 'primary' : 'muted'}>{roleLabel(u.role)}</Badge>
              <div className={styles.userPerms}>
                <code>{u.permissions.length}</code> perm{u.permissions.length > 1 ? 's' : ''}
                {' · '}
                <code>{u.maxDevEnvs === undefined ? '∞' : u.maxDevEnvs}</code> env{(u.maxDevEnvs ?? 2) > 1 ? 's' : ''}
              </div>
              <div className={styles.userActions}>
                <Button variant="ghost" size="sm" leftIcon={<Icon name="settings" size={12} />} onClick={() => setEditUserId(u.id)} disabled={!canAdmin}>
                  Gérer
                </Button>
                {u.id !== user.id && (
                  <Button variant="ghost" size="sm" leftIcon={<Icon name="x" size={12} />} onClick={() => setDeleteUserTarget(u.id)} disabled={!canAdmin}>
                    Supprimer
                  </Button>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* -------- Mon profil -------- */}
      <section className={styles.section}>
        <header className={styles.sectionHeader}>
          <div>
            <h2><Icon name="eye" size={16} /> Mes permissions actives <span className={styles.sub}>(profil simulé)</span></h2>
            <p>Les profils disponibles permettent de tester ce que chaque rôle voit.</p>
          </div>
        </header>
        <div className={styles.card}>
          <div className={styles.userRowHeader}>
            <Avatar name={user.name} color={user.avatarColor} size={48} />
            <div style={{ flex: 1 }}>
              <div className={styles.userName}>{user.name}</div>
              <div className={styles.userEmail}>{user.email}</div>
              <Badge tone="primary">{roleLabel(user.role)}</Badge>
            </div>
            <Button variant="secondary" size="sm" leftIcon={<Icon name="lock" size={12} />}
              onClick={() => setChangePasswordOpen(true)}>
              Changer mon mot de passe
            </Button>
          </div>
          <div className={styles.permGroups}>
            {PERMISSION_GROUPS.map((g) => (
              <div key={g.label} className={styles.permGroup}>
                <h4>{g.label}</h4>
                <div className={styles.permChips}>
                  {g.items.map((p) => {
                    const has = user.permissions.includes(p.key);
                    return (
                      <Badge key={p.key} tone={has ? 'success' : 'muted'}>
                        <Icon name={has ? 'check' : 'x'} size={9} /> {p.label}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* -------- Modales -------- */}
      {dotnetOpen && <DotnetModal onClose={() => setDotnetOpen(false)} />}
      {cdnOpen && <CdnConfigModal projectId={currentProject.id} projectName={currentProject.name} onClose={() => setCdnOpen(false)} />}
      {pteroOpen && <PteroConfigModal projectId={currentProject.id} projectName={currentProject.name} onClose={() => setPteroOpen(false)} />}
      {pteroApiOpen && <PteroApiConfigModal projectId={currentProject.id} projectName={currentProject.name} onClose={() => setPteroApiOpen(false)} />}
      {groupsOpen && groupsEnabled && (
        <ProjectGroupsModal projectId={currentProject.id} projectName={currentProject.name}
          onClose={() => setGroupsOpen(false)} onSaved={setGameGroups} />
      )}
      {procedureOpen && <ProcedureEditorModal projectId={currentProject.id} projectName={currentProject.name} onClose={() => setProcedureOpen(false)} />}
      {changePasswordOpen && <ChangePasswordModal onClose={() => setChangePasswordOpen(false)} />}
      {editProjectId && <ProjectEditModal projectId={editProjectId} onClose={() => setEditProjectId(null)} />}
      {createProjectOpen && <ProjectEditModal onClose={() => setCreateProjectOpen(false)} />}
      {deleteProjectTarget && <DeleteProjectModal projectId={deleteProjectTarget} onClose={() => setDeleteProjectTarget(null)} />}
      {editUserId && <UserEditModal userId={editUserId} onClose={() => setEditUserId(null)} />}
      {createUserOpen && <UserEditModal onClose={() => setCreateUserOpen(false)} />}
      {deleteUserTarget && <DeleteUserModal userId={deleteUserTarget} onClose={() => setDeleteUserTarget(null)} />}
    </div>
  );
}

function roleLabel(role: Role) {
  switch (role) {
    case 'admin': return 'Admin';
    case 'developer': return 'Développeur';
    case 'project-manager': return 'Gestionnaire';
    case 'viewer': return 'Invité';
  }
}

// ============================================================
// Project edit / create modal (exporté pour ProjectSwitcher)
// ============================================================
export function ProjectEditModal({ projectId, onClose }: { projectId?: string; onClose: () => void }) {
  const projects = useSessionStore((s) => s.projects);
  const updateProject = useSessionStore((s) => s.updateProject);
  const createProject = useSessionStore((s) => s.createProject);
  const existing = projectId ? projects.find((p) => p.id === projectId) : undefined;
  const isCreate = !existing;

  const [name, setName] = useState(existing?.name ?? '');
  const [code, setCode] = useState(existing?.code ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [color, setColor] = useState(existing?.color ?? '#3B82F6');
  const [dotnet, setDotnet] = useState(existing?.dotnetVersion ?? '9.0.304');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const valid = !!name.trim() && !!code.trim();
  const canSave = valid && (isCreate ||
    name !== existing!.name ||
    code !== existing!.code ||
    description !== existing!.description ||
    color !== existing!.color ||
    dotnet !== existing!.dotnetVersion
  );

  const save = async () => {
    setSaveError(null);
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        code: code.trim().toUpperCase(),
        description: description.trim(),
        color,
        dotnetVersion: dotnet.trim(),
      };
      if (isCreate) await createProject(payload);
      else await updateProject(existing!.id, payload);
      onClose();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Erreur inconnue');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open onClose={onClose}
      title={isCreate ? 'Nouveau projet' : `Configurer ${existing!.name}`}
      subtitle={isCreate ? 'Crée un nouveau projet avec ses containers et équipes' : 'Modifier les paramètres du projet'}
      width="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Annuler</Button>
          <Button variant="primary" onClick={save} disabled={!canSave || saving} loading={saving}>
            {isCreate ? 'Créer le projet' : 'Sauvegarder'}
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        {saveError && (
          <div style={{
            padding: '10px 12px',
            background: 'var(--color-danger-bg)',
            color: 'var(--color-danger)',
            borderRadius: 8,
            fontSize: 13,
          }}>
            {saveError}
          </div>
        )}
        <div className={styles.formCols}>
          <label className={styles.field}>
            <span>Nom <em>requis</em></span>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Paladium Bedrock" />
          </label>
          <label className={styles.field}>
            <span>Code <em>requis</em></span>
            <input type="text" value={code} onChange={(e) => setCode(e.target.value)} placeholder="PBE" maxLength={5} />
          </label>
        </div>

        <label className={styles.field}>
          <span>Description</span>
          <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description du projet…" />
        </label>

        <div className={styles.field}>
          <span>Couleur d'accent</span>
          <div className={styles.colorRow}>
            {AVATAR_COLORS.map((c) => (
              <button
                key={c}
                className={[styles.colorSwatch, color === c && styles.colorSwatchActive].filter(Boolean).join(' ')}
                style={{ background: c }}
                onClick={() => setColor(c)}
                type="button"
              />
            ))}
          </div>
        </div>

        <label className={styles.field}>
          <span>Version .NET</span>
          <input type="text" value={dotnet} onChange={(e) => setDotnet(e.target.value)} placeholder="9.0.304" />
        </label>
      </div>
    </Modal>
  );
}

function DeleteProjectModal({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const projects = useSessionStore((s) => s.projects);
  const deleteProject = useSessionStore((s) => s.deleteProject);
  const p = projects.find((x) => x.id === projectId);
  const [confirmed, setConfirmed] = useState(false);
  if (!p) return null;
  return (
    <Modal
      open onClose={onClose}
      title="Supprimer le projet"
      subtitle={p.name}
      width="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button variant="danger" onClick={async () => { await deleteProject(projectId); onClose(); }} disabled={!confirmed}>
            Supprimer définitivement
          </Button>
        </>
      }
    >
      <div className={styles.modalBody}>
        <p>La suppression de <strong>{p.name}</strong> déréférence ses containers, envs et APIs associés (ils ne seront plus visibles).</p>
        <p className={styles.muted}>Action journalisée. Dans le vrai système cela déclencherait aussi la libération des ressources infra.</p>
        <Toggle checked={confirmed} onChange={setConfirmed} label="Je confirme la suppression" />
      </div>
    </Modal>
  );
}

// ============================================================
// User edit / create / delete modals
// ============================================================
function UserEditModal({ userId, onClose }: { userId?: string; onClose: () => void }) {
  const users = useSessionStore((s) => s.users);
  const createUser = useSessionStore((s) => s.createUser);
  const updateUser = useSessionStore((s) => s.updateUser);
  const existing = userId ? users.find((u) => u.id === userId) : undefined;
  const isCreate = !existing;

  const [name, setName] = useState(existing?.name ?? '');
  const [email, setEmail] = useState(existing?.email ?? '');
  const [role, setRole] = useState<Role>(existing?.role ?? 'developer');
  const [avatarColor, setAvatarColor] = useState(existing?.avatarColor ?? AVATAR_COLORS[0]);
  const [permissions, setPermissions] = useState<Set<Permission>>(new Set(existing?.permissions ?? ['cicd.view']));
  // Nombre max d'envs de dev : '' = illimité, chiffre = quota, '0' = aucun
  const [maxDevEnvs, setMaxDevEnvs] = useState<string>(
    existing?.maxDevEnvs === undefined ? '' : String(existing.maxDevEnvs)
  );
  const [initialPassword, setInitialPassword] = useState<string>('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const togglePerm = (p: Permission) => {
    setPermissions((s) => { const n = new Set(s); if (n.has(p)) n.delete(p); else n.add(p); return n; });
  };

  const allPermCount = useMemo(
    () => PERMISSION_GROUPS.reduce((a, g) => a + g.items.length, 0),
    [],
  );
  const selectAllPerms = () => setPermissions(new Set(PERMISSION_GROUPS.flatMap((g) => g.items.map((i) => i.key))));
  const clearAllPerms = () => setPermissions(new Set(['cicd.view']));

  // Presets par rôle
  const applyRolePreset = (r: Role) => {
    setRole(r);
    if (r === 'admin') selectAllPerms();
    else if (r === 'project-manager') setPermissions(new Set(['cicd.view', 'cicd.logs', 'cicd.deploy.beta', 'cicd.deploy.preprod', 'cicd.schedule', 'cicd.resourcepack.release', 'cicd.network']));
    else if (r === 'developer') setPermissions(new Set(['cicd.view', 'cicd.logs', 'cicd.deploy.beta', 'cicd.restart']));
    else if (r === 'viewer') setPermissions(new Set(['cicd.view', 'cicd.logs']));
  };

  const validPassword = !isCreate || initialPassword.length >= 8;
  const valid = !!name.trim() && !!email.trim() && /.+@.+\..+/.test(email) && validPassword;

  const save = async () => {
    setSaveError(null);
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        email: email.trim(),
        role,
        avatarColor,
        permissions: Array.from(permissions),
        maxDevEnvs: maxDevEnvs === '' ? undefined : Math.max(0, Number(maxDevEnvs)),
      };
      if (isCreate) {
        await createUser({ ...payload, initialPassword });
      } else {
        await updateUser(existing!.id, payload);
      }
      onClose();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Erreur inconnue');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open onClose={onClose}
      title={isCreate ? 'Nouvel utilisateur' : `Gérer ${existing!.name}`}
      subtitle={isCreate ? 'Créer un compte et définir ses permissions' : 'Modifier le rôle et les permissions'}
      width="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Annuler</Button>
          <Button variant="primary" onClick={save} disabled={!valid || saving} loading={saving}>
            {isCreate ? 'Créer l\'utilisateur' : 'Sauvegarder'}
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        {saveError && (
          <div style={{
            padding: '10px 12px',
            background: 'var(--color-danger-bg)',
            color: 'var(--color-danger)',
            borderRadius: 8,
            fontSize: 13,
          }}>
            {saveError}
          </div>
        )}
        <div className={styles.userPreview}>
          <Avatar name={name || '??'} color={avatarColor} size={48} />
          <div>
            <div className={styles.userPreviewName}>{name || '—'}</div>
            <div className={styles.userPreviewEmail}>{email || '—'}</div>
            <Badge tone="primary">{roleLabel(role)}</Badge>
          </div>
        </div>

        <div className={styles.formCols}>
          <label className={styles.field}>
            <span>Nom complet <em>requis</em></span>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Lucas Dev" />
          </label>
          <label className={styles.field}>
            <span>Email <em>requis</em></span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="lucas@paladium.games" />
          </label>
        </div>

        {isCreate && (
          <label className={styles.field}>
            <span>Mot de passe initial <em>min. 8 caractères</em></span>
            <input
              type="text"
              value={initialPassword}
              onChange={(e) => setInitialPassword(e.target.value)}
              placeholder="À transmettre à l'utilisateur"
              autoComplete="new-password"
            />
          </label>
        )}

        <div className={styles.field}>
          <span>Couleur d'avatar</span>
          <div className={styles.colorRow}>
            {AVATAR_COLORS.map((c) => (
              <button
                key={c}
                className={[styles.colorSwatch, avatarColor === c && styles.colorSwatchActive].filter(Boolean).join(' ')}
                style={{ background: c }}
                onClick={() => setAvatarColor(c)}
                type="button"
              />
            ))}
          </div>
        </div>

        <div className={styles.field}>
          <span>Quota d'envs de dev</span>
          <div className={styles.quotaRow}>
            <label className={[styles.quotaOpt, maxDevEnvs === '' && styles.quotaOptActive].filter(Boolean).join(' ')}>
              <input type="radio" checked={maxDevEnvs === ''} onChange={() => setMaxDevEnvs('')} />
              <strong>Illimité</strong>
              <span>Pas de limite</span>
            </label>
            <label className={[styles.quotaOpt, maxDevEnvs === '0' && styles.quotaOptActive].filter(Boolean).join(' ')}>
              <input type="radio" checked={maxDevEnvs === '0'} onChange={() => setMaxDevEnvs('0')} />
              <strong>Aucun</strong>
              <span>Ne peut pas créer d'env</span>
            </label>
            <label className={[styles.quotaOpt, styles.quotaOptCustom, maxDevEnvs !== '' && maxDevEnvs !== '0' && styles.quotaOptActive].filter(Boolean).join(' ')}>
              <input
                type="radio"
                checked={maxDevEnvs !== '' && maxDevEnvs !== '0'}
                onChange={() => setMaxDevEnvs('3')}
              />
              <strong>Quota fixe</strong>
              <input
                type="number"
                min={1}
                className={styles.quotaInput}
                value={maxDevEnvs !== '' && maxDevEnvs !== '0' ? maxDevEnvs : ''}
                onChange={(e) => setMaxDevEnvs(e.target.value)}
                onFocus={() => { if (maxDevEnvs === '' || maxDevEnvs === '0') setMaxDevEnvs('3'); }}
                placeholder="3"
              />
            </label>
          </div>
        </div>

        <div className={styles.field}>
          <span>Rôle · applique un preset de permissions</span>
          <div className={styles.rolePicker}>
            {ROLE_OPTIONS.map((r) => (
              <button
                key={r.value}
                type="button"
                className={[styles.roleTile, role === r.value && styles.roleTileActive].filter(Boolean).join(' ')}
                onClick={() => applyRolePreset(r.value)}
              >
                <strong>{r.label}</strong>
                <span>{r.description}</span>
              </button>
            ))}
          </div>
        </div>

        <div className={styles.field}>
          <div className={styles.permHeader}>
            <span>Permissions · <strong>{permissions.size}</strong>/{allPermCount} actives</span>
            <div>
              <button type="button" className={styles.link} onClick={selectAllPerms}>Tout cocher</button>
              <span className={styles.sep} />
              <button type="button" className={styles.link} onClick={clearAllPerms}>Minimum</button>
            </div>
          </div>
          <div className={styles.permMatrix}>
            {PERMISSION_GROUPS.map((g) => (
              <div key={g.label} className={styles.permCategory}>
                <div className={styles.permCategoryLabel}>{g.label}</div>
                {g.items.map((p) => {
                  const on = permissions.has(p.key);
                  return (
                    <label key={p.key} className={[styles.permRow, on && styles.permRowOn].filter(Boolean).join(' ')}>
                      <input type="checkbox" checked={on} onChange={() => togglePerm(p.key)} />
                      <span className={styles.permCheck}>{on && <Icon name="check" size={10} />}</span>
                      <div className={styles.permText}>
                        <span className={styles.permLabel}>{p.label}</span>
                        {p.hint && <span className={styles.permHint}>{p.hint}</span>}
                      </div>
                      <code className={styles.permKey}>{p.key}</code>
                    </label>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}

function DeleteUserModal({ userId, onClose }: { userId: string; onClose: () => void }) {
  const users = useSessionStore((s) => s.users);
  const deleteUser = useSessionStore((s) => s.deleteUser);
  const u = users.find((x) => x.id === userId);
  if (!u) return null;
  return (
    <Modal
      open onClose={onClose}
      title="Supprimer l'utilisateur"
      subtitle={u.name}
      width="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button variant="danger" onClick={async () => { await deleteUser(userId); onClose(); }}>
            Supprimer
          </Button>
        </>
      }
    >
      <div className={styles.modalBody}>
        <p>L'utilisateur <strong>{u.name}</strong> perdra tout accès. Ses envs de dev existants resteront visibles dans l'historique.</p>
      </div>
    </Modal>
  );
}

// ============================================================
// .NET modal (inchangée, juste extraite)
// ============================================================
const DOTNET_VERSIONS = ['8.0.11', '8.0.12', '9.0.304', '9.0.305'];

function DotnetModal({ onClose }: { onClose: () => void }) {
  const currentProject = useSessionStore((s) => s.currentProject());
  const updateProject = useSessionStore((s) => s.updateProject);
  const [selected, setSelected] = useState(currentProject.dotnetVersion);
  const [confirmed, setConfirmed] = useState(false);

  const save = async () => {
    await updateProject(currentProject.id, { dotnetVersion: selected });
    onClose();
  };

  return (
    <Modal
      open onClose={onClose}
      title="Modifier la version .NET"
      subtitle={`Global à ${currentProject.name} · tous les containers rebuild au prochain déploiement`}
      width="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button variant="primary" onClick={save} disabled={selected === currentProject.dotnetVersion || !confirmed}>
            Appliquer
          </Button>
        </>
      }
    >
      <div className={styles.dotnetBox}>
        <div className={styles.dotnetList}>
          {DOTNET_VERSIONS.map((v) => (
            <label key={v} className={[styles.dotnetRow, selected === v && styles.dotnetActive].filter(Boolean).join(' ')}>
              <input type="radio" name="dotnet" checked={selected === v} onChange={() => setSelected(v)} />
              <span className={styles.dotnetRadio} aria-hidden>{selected === v && <span className={styles.dotnetRadioInner} />}</span>
              <span className={styles.mono}>.NET {v}</span>
              {v === currentProject.dotnetVersion && <Badge tone="primary">Actuelle</Badge>}
              {v.startsWith('9') && <Badge tone="info">Recommandée</Badge>}
            </label>
          ))}
        </div>

        <div className={styles.dotnetWarn}>
          <Icon name="warn" size={14} />
          <div>
            <strong>Cette modification impacte toute la prod.</strong><br />
            Les containers MC seront rebuild au prochain déploiement.
            Les APIs passées en container seront aussi concernées.
          </div>
        </div>

        <Toggle
          checked={confirmed}
          onChange={setConfirmed}
          label="Je confirme la modification globale"
          description="Cette action sera journalisée et visible dans l'historique."
        />
      </div>
    </Modal>
  );
}

// Helper non exporté ailleurs — conserve la compat avec le role param
function _assertRole(r: Role): r is Role { return !!r; }
void _assertRole;

// ============================================================
// CDN config modal — onglets par env (dev/beta/preprod/prod)
// Chaque env a sa propre config CDN. Save = save l'onglet courant.
// ============================================================
function CdnConfigModal({ projectId, projectName, onClose }: { projectId: string; projectName: string; onClose: () => void }) {
  const ENVS: { id: 'dev' | 'beta' | 'preprod' | 'prod'; label: string }[] = [
    { id: 'dev', label: 'Dev' },
    { id: 'beta', label: 'Beta' },
    { id: 'preprod', label: 'Pré-prod' },
    { id: 'prod', label: 'Prod' },
  ];
  type EnvId = (typeof ENVS)[number]['id'];

  const emptyCfg: import('@/deploy/services/cdn-config.service').CdnConfig = {
    enabled: false, sftpHost: '', sftpPort: 22, sftpUser: '',
    sftpPassword: '', sftpPrivateKey: '',
    basePath: '/', pathTemplate: '{name}/{version}/{filename}',
    cacheClearCommand: '',
  };

  const [configs, setConfigs] = useState<Record<EnvId, import('@/deploy/services/cdn-config.service').CdnConfig>>({
    dev: emptyCfg, beta: emptyCfg, preprod: emptyCfg, prod: emptyCfg,
  });
  const [currentEnv, setCurrentEnv] = useState<EnvId>('preprod');
  const [authMethod, setAuthMethod] = useState<Record<EnvId, 'key' | 'password'>>({
    dev: 'password', beta: 'password', preprod: 'password', prod: 'password',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const { cdnConfigService } = await import('@/deploy/services/cdn-config.service');
        const all = await cdnConfigService.listAll(projectId);
        const next: Record<EnvId, import('@/deploy/services/cdn-config.service').CdnConfig> = { ...configs };
        const nextAuth: Record<EnvId, 'key' | 'password'> = { ...authMethod };
        for (const e of ENVS) {
          const c = all[e.id] ?? emptyCfg;
          next[e.id] = { ...emptyCfg, ...c };
          nextAuth[e.id] = c.sftpPrivateKey ? 'key' : 'password';
        }
        setConfigs(next);
        setAuthMethod(nextAuth);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Erreur de chargement');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const updateCurrent = (patch: Partial<import('@/deploy/services/cdn-config.service').CdnConfig>) => {
    setConfigs((prev) => ({ ...prev, [currentEnv]: { ...prev[currentEnv], ...patch } }));
  };
  const updateAuthMethod = (m: 'key' | 'password') => {
    setAuthMethod((prev) => ({ ...prev, [currentEnv]: m }));
  };

  const saveCurrent = async () => {
    setError(null);
    setSavedNotice(null);
    setSaving(true);
    try {
      const { cdnConfigService } = await import('@/deploy/services/cdn-config.service');
      const cfg = configs[currentEnv];
      const method = authMethod[currentEnv];
      await cdnConfigService.update(projectId, currentEnv, {
        enabled: cfg.enabled,
        sftpHost: cfg.sftpHost?.trim() || undefined,
        sftpPort: Number(cfg.sftpPort) || 22,
        sftpUser: cfg.sftpUser?.trim() || undefined,
        // On envoie seulement la méthode active, l'autre est wipée côté backend
        sftpPassword: method === 'password' ? (cfg.sftpPassword?.trim() || undefined) : undefined,
        sftpPrivateKey: method === 'key' ? (cfg.sftpPrivateKey?.trim() || undefined) : undefined,
        basePath: cfg.basePath?.trim() || '/',
        pathTemplate: cfg.pathTemplate?.trim() || '{name}/{version}/{filename}',
        cacheClearCommand: cfg.cacheClearCommand?.trim() || undefined,
      });
      setSavedNotice(`Config ${currentEnv} sauvegardée ✓`);
      setTimeout(() => setSavedNotice(null), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur inconnue');
    } finally {
      setSaving(false);
    }
  };

  const cfg = configs[currentEnv];
  const method = authMethod[currentEnv];

  return (
    <Modal
      open onClose={onClose}
      title="Configuration CDN par environnement"
      subtitle={`${projectName} · une config SFTP par env (dev/beta/preprod/prod)`}
      width="lg"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={saving}>Fermer</Button>
        <Button variant="primary" onClick={saveCurrent} disabled={loading || saving} loading={saving}>
          Sauvegarder {currentEnv}
        </Button>
      </>}
    >
      <div className={styles.form}>
        {/* Onglets d'envs */}
        <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--color-border)', marginBottom: 4 }}>
          {ENVS.map((e) => {
            const c = configs[e.id];
            return (
              <button
                key={e.id}
                type="button"
                onClick={() => setCurrentEnv(e.id)}
                style={{
                  padding: '8px 14px',
                  background: currentEnv === e.id ? 'var(--color-surface)' : 'transparent',
                  border: `1px solid ${currentEnv === e.id ? 'var(--color-border)' : 'transparent'}`,
                  borderBottom: currentEnv === e.id ? '1px solid var(--color-surface)' : 'none',
                  borderRadius: '6px 6px 0 0',
                  marginBottom: -1,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  color: currentEnv === e.id ? 'var(--color-text)' : 'var(--color-text-muted)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {e.label}
                {c.enabled && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--color-success)' }} />}
              </button>
            );
          })}
        </div>

        {error && <div style={{ padding: '10px 12px', background: 'var(--color-danger-bg)', color: 'var(--color-danger)', borderRadius: 8, fontSize: 13 }}>{error}</div>}
        {savedNotice && <div style={{ padding: '10px 12px', background: 'var(--color-success-bg)', color: 'var(--color-success)', borderRadius: 8, fontSize: 13 }}>{savedNotice}</div>}

        {loading ? (
          <div style={{ color: 'var(--color-text-muted)' }}>Chargement…</div>
        ) : (
          <>
            <Toggle
              checked={cfg.enabled}
              onChange={(v) => updateCurrent({ enabled: v })}
              label={`CDN activé pour ${currentEnv}`}
              description="Si désactivé, les uploads CDN pour cet env sont skippés (warning, pas une erreur)."
            />

            <div className={styles.formCols}>
              <label className={styles.field}>
                <span>Hôte SFTP</span>
                <input type="text" value={cfg.sftpHost ?? ''} onChange={(e) => updateCurrent({ sftpHost: e.target.value })} placeholder="cdn-preprod.paladium.games" />
              </label>
              <label className={styles.field}>
                <span>Port</span>
                <input type="number" value={cfg.sftpPort ?? 22} onChange={(e) => updateCurrent({ sftpPort: Number(e.target.value) || 22 })} />
              </label>
            </div>

            <label className={styles.field}>
              <span>Utilisateur SSH</span>
              <input type="text" value={cfg.sftpUser ?? ''} onChange={(e) => updateCurrent({ sftpUser: e.target.value })} placeholder="deploy" />
            </label>

            <div className={styles.field}>
              <span>Méthode d'authentification</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <label style={{ flex: 1, padding: '10px 14px', border: '1px solid var(--color-border)', borderRadius: 6, cursor: 'pointer', background: method === 'key' ? 'var(--color-primary-light)' : 'transparent' }}>
                  <input type="radio" checked={method === 'key'} onChange={() => updateAuthMethod('key')} style={{ marginRight: 8 }} />
                  <strong>Clé SSH privée</strong> · saisie directe
                </label>
                <label style={{ flex: 1, padding: '10px 14px', border: '1px solid var(--color-border)', borderRadius: 6, cursor: 'pointer', background: method === 'password' ? 'var(--color-primary-light)' : 'transparent' }}>
                  <input type="radio" checked={method === 'password'} onChange={() => updateAuthMethod('password')} style={{ marginRight: 8 }} />
                  <strong>Mot de passe</strong> · saisie directe
                </label>
              </div>
            </div>

            {method === 'key' && (
              <label className={styles.field}>
                <span>Clé privée SSH <em>format PEM (multiligne) — stockée chiffrée en Mongo</em></span>
                <textarea
                  rows={8}
                  value={cfg.sftpPrivateKey ?? ''}
                  onChange={(e) => updateCurrent({ sftpPrivateKey: e.target.value })}
                  placeholder={'-----BEGIN OPENSSH PRIVATE KEY-----\n...\n-----END OPENSSH PRIVATE KEY-----'}
                  style={{ fontFamily: 'var(--font-family-mono)', fontSize: 11, minHeight: 140 }}
                />
              </label>
            )}
            {method === 'password' && (
              <label className={styles.field}>
                <span>Mot de passe SSH <em>stocké en Mongo (texte clair pour beta — à durcir en prod)</em></span>
                <input
                  type="password"
                  value={cfg.sftpPassword ?? ''}
                  onChange={(e) => updateCurrent({ sftpPassword: e.target.value })}
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
              </label>
            )}

            <label className={styles.field}>
              <span>Path de base sur le CDN</span>
              <input type="text" value={cfg.basePath ?? '/'} onChange={(e) => updateCurrent({ basePath: e.target.value })} placeholder="/var/www/cdn/packs/" />
            </label>

            <label className={styles.field}>
              <span>Template du chemin <em>tokens : {'{name}'} {'{version}'} {'{env}'} {'{filename}'}</em></span>
              <input type="text" value={cfg.pathTemplate ?? ''} onChange={(e) => updateCurrent({ pathTemplate: e.target.value })} placeholder="{name}/{version}/{filename}" />
            </label>

            <label className={styles.field}>
              <span>Commande SSH après upload <em>optionnelle (cache clear)</em></span>
              <input type="text" value={cfg.cacheClearCommand ?? ''} onChange={(e) => updateCurrent({ cacheClearCommand: e.target.value })} placeholder="sudo /usr/local/bin/clear-cache.sh" />
            </label>
          </>
        )}
      </div>
    </Modal>
  );
}

// ============================================================
// Pterodactyl config modal — onglets preprod / prod uniquement
// ============================================================
function PteroConfigModal({ projectId, projectName, onClose }: { projectId: string; projectName: string; onClose: () => void }) {
  const ENVS: { id: 'dev' | 'beta' | 'preprod' | 'prod'; label: string }[] = [
    { id: 'dev', label: 'Dev' },
    { id: 'beta', label: 'Bêta' },
    { id: 'preprod', label: 'Pré-prod' },
    { id: 'prod', label: 'Prod' },
  ];
  type EnvId = (typeof ENVS)[number]['id'];
  const emptyCfg: import('@/deploy/services/pterodactyl-config.service').PterodactylConfig = {
    enabled: false, panelUrl: '', ptla: '', ptlc: '',
    dbHost: '', dbPort: 3306, dbName: '', dbUser: '', dbPassword: '',
  };

  const [configs, setConfigs] = useState<Record<EnvId, import('@/deploy/services/pterodactyl-config.service').PterodactylConfig>>({
    dev: emptyCfg, beta: emptyCfg, preprod: emptyCfg, prod: emptyCfg,
  });
  const [currentEnv, setCurrentEnv] = useState<EnvId>('preprod');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dbTesting, setDbTesting] = useState(false);
  const [dbTestResult, setDbTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const { pterodactylConfigService } = await import('@/deploy/services/pterodactyl-config.service');
        const all = await pterodactylConfigService.listAll(projectId);
        const next: Record<EnvId, import('@/deploy/services/pterodactyl-config.service').PterodactylConfig> = { ...configs };
        for (const e of ENVS) next[e.id] = { ...emptyCfg, ...(all[e.id] ?? {}) };
        setConfigs(next);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Erreur de chargement');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const updateCurrent = (patch: Partial<import('@/deploy/services/pterodactyl-config.service').PterodactylConfig>) => {
    setConfigs((prev) => ({ ...prev, [currentEnv]: { ...prev[currentEnv], ...patch } }));
  };

  const save = async () => {
    setError(null); setNotice(null); setSaving(true);
    try {
      const { pterodactylConfigService } = await import('@/deploy/services/pterodactyl-config.service');
      await pterodactylConfigService.update(projectId, currentEnv, configs[currentEnv]);
      setNotice(`Config Pterodactyl ${currentEnv} sauvegardée ✓`);
      setTimeout(() => setNotice(null), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur inconnue');
    } finally {
      setSaving(false);
    }
  };

  const testDb = async () => {
    setDbTesting(true);
    setDbTestResult(null);
    try {
      const { pterodactylConfigService } = await import('@/deploy/services/pterodactyl-config.service');
      const res = await pterodactylConfigService.testDb(projectId, currentEnv, configs[currentEnv]);
      setDbTestResult(res);
    } catch (e) {
      setDbTestResult({ ok: false, message: e instanceof Error ? e.message : 'Erreur inconnue' });
    } finally {
      setDbTesting(false);
    }
  };

  const cfg = configs[currentEnv];

  return (
    <Modal
      open onClose={onClose}
      title="Configuration Pterodactyl par environnement"
      subtitle={`${projectName} · PTLA admin + PTLC client par env (preprod/prod uniquement)`}
      width="lg"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={saving}>Fermer</Button>
        <Button variant="primary" onClick={save} disabled={loading || saving} loading={saving}>
          Sauvegarder {currentEnv}
        </Button>
      </>}
    >
      <div className={styles.form}>
        <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--color-border)', marginBottom: 4 }}>
          {ENVS.map((e) => {
            const c = configs[e.id];
            return (
              <button key={e.id} type="button" onClick={() => setCurrentEnv(e.id)}
                style={{
                  padding: '8px 14px',
                  background: currentEnv === e.id ? 'var(--color-surface)' : 'transparent',
                  border: `1px solid ${currentEnv === e.id ? 'var(--color-border)' : 'transparent'}`,
                  borderBottom: currentEnv === e.id ? '1px solid var(--color-surface)' : 'none',
                  borderRadius: '6px 6px 0 0', marginBottom: -1, cursor: 'pointer',
                  fontSize: 14, fontWeight: 600,
                  color: currentEnv === e.id ? 'var(--color-text)' : 'var(--color-text-muted)',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}>
                {e.label}
                {c.enabled && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--color-success)' }} />}
              </button>
            );
          })}
        </div>

        {error && <div style={{ padding: '10px 12px', background: 'var(--color-danger-bg)', color: 'var(--color-danger)', borderRadius: 8, fontSize: 13 }}>{error}</div>}
        {notice && <div style={{ padding: '10px 12px', background: 'var(--color-success-bg)', color: 'var(--color-success)', borderRadius: 8, fontSize: 13 }}>{notice}</div>}

        {loading ? (
          <div style={{ color: 'var(--color-text-muted)' }}>Chargement…</div>
        ) : (
          <>
            <Toggle
              checked={cfg.enabled}
              onChange={(v) => updateCurrent({ enabled: v })}
              label={`Pterodactyl activé pour ${currentEnv}`}
              description="Si désactivé, le bouton 'Promouvoir' refusera de lancer la procédure."
            />

            <label className={styles.field}>
              <span>URL du panel <em>sans slash final</em></span>
              <input type="text" value={cfg.panelUrl ?? ''} onChange={(e) => updateCurrent({ panelUrl: e.target.value })}
                placeholder="https://ptero.paladium.games" />
            </label>

            <label className={styles.field}>
              <span>PTLA — clé Application <em>Bearer admin (listing + update servers)</em></span>
              <input type="password" autoComplete="new-password"
                value={cfg.ptla ?? ''} onChange={(e) => updateCurrent({ ptla: e.target.value })}
                placeholder="ptla_..." />
            </label>

            <label className={styles.field}>
              <span>PTLC — clé Client <em>commandes console + signaux power</em></span>
              <input type="password" autoComplete="new-password"
                value={cfg.ptlc ?? ''} onChange={(e) => updateCurrent({ ptlc: e.target.value })}
                placeholder="ptlc_..." />
            </label>

            <div style={{ padding: '8px 12px', background: 'var(--color-info-bg)', color: 'var(--color-info)', borderRadius: 8, fontSize: 12 }}>
              <Icon name="rocket" size={12} /> La clé PTLA doit avoir les permissions admin pour <strong>lister tous les serveurs</strong> du panel.
              La clé PTLC est utilisée pour envoyer des commandes console + signaux power.
            </div>

            {/* ============ Section DB MySQL (bypass eggs read-only) ============ */}
            <div style={{
              marginTop: 12,
              padding: 12,
              background: 'var(--color-background-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              display: 'flex', flexDirection: 'column', gap: 8,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Icon name="settings" size={14} />
                <strong style={{ fontSize: 13 }}>Connexion MySQL Pterodactyl <em style={{ fontStyle: 'normal', fontSize: 11, opacity: 0.7, fontWeight: 400 }}>(optionnelle — requise pour update les eggs)</em></strong>
              </div>
              <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                L'API Application Pterodactyl est <strong>read-only</strong> sur les eggs (HTTP 405 sur PATCH).
                Sans cette config DB, l'auto-update des images d'eggs lors d'une promotion sera skippé avec un warning.
                Une fois les creds renseignés, on UPDATE directement la colonne <code>docker_images</code> de la table <code>eggs</code>.
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <label className={styles.field} style={{ flex: 3 }}>
                  <span>Host MySQL</span>
                  <input type="text" value={cfg.dbHost ?? ''} onChange={(e) => updateCurrent({ dbHost: e.target.value })}
                    placeholder="panel-mysql.internal ou 127.0.0.1" />
                </label>
                <label className={styles.field} style={{ flex: 1 }}>
                  <span>Port</span>
                  <input type="number" value={cfg.dbPort ?? 3306} onChange={(e) => updateCurrent({ dbPort: Number(e.target.value) || 3306 })} />
                </label>
              </div>

              <label className={styles.field}>
                <span>Database</span>
                <input type="text" value={cfg.dbName ?? ''} onChange={(e) => updateCurrent({ dbName: e.target.value })}
                  placeholder="panel" />
              </label>

              <div style={{ display: 'flex', gap: 8 }}>
                <label className={styles.field} style={{ flex: 1 }}>
                  <span>User</span>
                  <input type="text" autoComplete="new-password" value={cfg.dbUser ?? ''} onChange={(e) => updateCurrent({ dbUser: e.target.value })}
                    placeholder="pterodactyl" />
                </label>
                <label className={styles.field} style={{ flex: 1 }}>
                  <span>Password</span>
                  <input type="password" autoComplete="new-password" value={cfg.dbPassword ?? ''} onChange={(e) => updateCurrent({ dbPassword: e.target.value })}
                    placeholder="••••••••" />
                </label>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Button variant="secondary" size="sm" onClick={() => void testDb()}
                  disabled={dbTesting || !cfg.dbHost || !cfg.dbName || !cfg.dbUser || !cfg.dbPassword}
                  loading={dbTesting}>
                  Tester la connexion
                </Button>
                {dbTestResult && (
                  <span style={{
                    fontSize: 12,
                    color: dbTestResult.ok ? 'var(--color-success)' : 'var(--color-danger)',
                  }}>
                    <Icon name={dbTestResult.ok ? 'check' : 'warn'} size={11} /> {dbTestResult.message}
                  </span>
                )}
              </div>
            </div>

            {/* Dev envs (containers MC) + beta slots — sur l'onglet dev du Ptero containers.
                Les dev envs et les 5 slots beta tournent tous sur ce Ptero (pas sur le Ptero APIs). */}
            {currentEnv === 'dev' && (
              <DevExtrasSection cfg={cfg} updateCurrent={updateCurrent} />
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

// ============================================================
// Procédure d'update — éditeur per env (preprod / prod)
// ============================================================
export function ProcedureEditorModal({ projectId, projectName, onClose }: { projectId: string; projectName: string; onClose: () => void }) {
  const ENVS: { id: 'preprod' | 'prod'; label: string }[] = [
    { id: 'preprod', label: 'Pré-prod' },
    { id: 'prod', label: 'Prod' },
  ];
  type EnvId = 'preprod' | 'prod';
  type ProcsByName = Record<string, import('@/deploy/services/procedure.service').UpdateProcedure>;
  const DEFAULT_NAME = 'Défaut';

  // proceduresByEnv[env][procName] = procédure
  const [proceduresByEnv, setProceduresByEnv] = useState<Record<EnvId, ProcsByName>>({
    preprod: {}, prod: {},
  });
  const [currentEnv, setCurrentEnv] = useState<EnvId>('prod');
  // currentProcName par env, pour ne pas perdre la sélection en switchant d'env
  const [procNameByEnv, setProcNameByEnv] = useState<Record<EnvId, string>>({
    preprod: DEFAULT_NAME, prod: DEFAULT_NAME,
  });
  const currentProcName = procNameByEnv[currentEnv];
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const { procedureService } = await import('@/deploy/services/procedure.service');
        const [preprod, prod] = await Promise.all([
          procedureService.listByName(projectId, 'preprod'),
          procedureService.listByName(projectId, 'prod'),
        ]);
        const ensure = (m: ProcsByName): ProcsByName => {
          if (Object.keys(m).length === 0) return { [DEFAULT_NAME]: { steps: [] } };
          return m;
        };
        const nextByEnv = { preprod: ensure(preprod), prod: ensure(prod) };
        setProceduresByEnv(nextByEnv);
        // Sélection initiale : la 1ère procédure dispo dans chaque env
        setProcNameByEnv({
          preprod: Object.keys(nextByEnv.preprod)[0] ?? DEFAULT_NAME,
          prod: Object.keys(nextByEnv.prod)[0] ?? DEFAULT_NAME,
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Erreur de chargement');
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId]);

  const procsForCurrentEnv = proceduresByEnv[currentEnv] ?? {};
  const procNames = Object.keys(procsForCurrentEnv);
  const current = procsForCurrentEnv[currentProcName] ?? { steps: [] };

  const setSteps = (steps: import('@/deploy/services/procedure.service').ProcedureStep[]) => {
    setProceduresByEnv((prev) => ({
      ...prev,
      [currentEnv]: { ...prev[currentEnv], [currentProcName]: { steps } },
    }));
  };

  const addStep = (type: import('@/deploy/services/procedure.service').StepType) => {
    const newStep: import('@/deploy/services/procedure.service').ProcedureStep = { type };
    if (type === 'sleep') newStep.sleepSeconds = 5;
    if (type === 'power') newStep.powerSignal = 'stop';
    if (type === 'cdn-promote') newStep.sourceEnv = 'preprod';
    setSteps([...current.steps, newStep]);
  };

  const updateStep = (idx: number, patch: Partial<import('@/deploy/services/procedure.service').ProcedureStep>) => {
    const next = [...current.steps];
    next[idx] = { ...next[idx], ...patch };
    setSteps(next);
  };

  const removeStep = (idx: number) => {
    setSteps(current.steps.filter((_, i) => i !== idx));
  };

  const moveStep = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= current.steps.length) return;
    const next = [...current.steps];
    [next[idx], next[target]] = [next[target], next[idx]];
    setSteps(next);
  };

  const resetDefault = async () => {
    if (!confirm(`Charger la procédure Paladium par défaut dans "${currentProcName}" (${currentEnv}) ?\nRien n'est sauvegardé tant que tu ne cliques pas sur "Sauvegarder".`)) return;
    try {
      const { procedureService } = await import('@/deploy/services/procedure.service');
      const def = await procedureService.getDefault(projectId);
      setProceduresByEnv((prev) => ({
        ...prev,
        [currentEnv]: { ...prev[currentEnv], [currentProcName]: def },
      }));
      setNotice('Procédure par défaut chargée — pense à sauvegarder.');
      setTimeout(() => setNotice(null), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    }
  };

  const createNewProcedure = () => {
    const name = window.prompt(`Nom de la nouvelle procédure ${currentEnv} ?`);
    if (!name || !name.trim()) return;
    const trimmed = name.trim();
    if (procsForCurrentEnv[trimmed]) {
      alert(`Une procédure "${trimmed}" existe déjà dans ${currentEnv}.`);
      return;
    }
    setProceduresByEnv((prev) => ({
      ...prev,
      [currentEnv]: { ...prev[currentEnv], [trimmed]: { steps: [] } },
    }));
    setProcNameByEnv((prev) => ({ ...prev, [currentEnv]: trimmed }));
    setNotice(`Procédure "${trimmed}" créée (vide) — ajoute des étapes puis sauvegarde.`);
    setTimeout(() => setNotice(null), 3500);
  };

  const deleteCurrent = async () => {
    if (procNames.length <= 1) {
      alert('Impossible de supprimer la dernière procédure de cet env.');
      return;
    }
    if (!confirm(`Supprimer définitivement la procédure "${currentProcName}" de ${currentEnv} ?`)) return;
    setSaving(true);
    try {
      const { procedureService } = await import('@/deploy/services/procedure.service');
      await procedureService.deleteByName(projectId, currentEnv, currentProcName);
      const remaining = { ...procsForCurrentEnv };
      delete remaining[currentProcName];
      setProceduresByEnv((prev) => ({ ...prev, [currentEnv]: remaining }));
      const nextName = Object.keys(remaining)[0] ?? DEFAULT_NAME;
      setProcNameByEnv((prev) => ({ ...prev, [currentEnv]: nextName }));
      setNotice('Procédure supprimée ✓');
      setTimeout(() => setNotice(null), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    setError(null); setNotice(null); setSaving(true);
    try {
      const { procedureService } = await import('@/deploy/services/procedure.service');
      await procedureService.saveByName(projectId, currentEnv, currentProcName, current);
      setNotice(`Procédure "${currentProcName}" (${currentEnv}) sauvegardée ✓`);
      setTimeout(() => setNotice(null), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur inconnue');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open onClose={onClose}
      title="Procédure de mise à jour"
      subtitle={`${projectName} · séquence d'étapes exécutée lors d'une promotion`}
      width="lg"
      footer={<>
        <Button variant="ghost" onClick={resetDefault} disabled={saving || loading}>Reset défaut Paladium</Button>
        <Button variant="ghost" onClick={onClose} disabled={saving}>Fermer</Button>
        <Button variant="primary" onClick={save} disabled={loading || saving} loading={saving}>
          Sauvegarder "{currentProcName}" ({currentEnv})
        </Button>
      </>}
    >
      <div className={styles.form}>
        <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--color-border)' }}>
          {ENVS.map((e) => {
            const procsInEnv = proceduresByEnv[e.id] ?? {};
            return (
              <button key={e.id} type="button" onClick={() => setCurrentEnv(e.id)}
                style={{
                  padding: '8px 14px',
                  background: currentEnv === e.id ? 'var(--color-surface)' : 'transparent',
                  border: `1px solid ${currentEnv === e.id ? 'var(--color-border)' : 'transparent'}`,
                  borderBottom: currentEnv === e.id ? '1px solid var(--color-surface)' : 'none',
                  borderRadius: '6px 6px 0 0', marginBottom: -1, cursor: 'pointer',
                  fontSize: 14, fontWeight: 600,
                  color: currentEnv === e.id ? 'var(--color-text)' : 'var(--color-text-muted)',
                }}>
                {e.label} <span style={{ opacity: 0.6, marginLeft: 4 }}>({Object.keys(procsInEnv).length} proc)</span>
              </button>
            );
          })}
        </div>

        {/* Règle du 2026-10-20 : l'ordre enregistré ne décide plus de l'arrêt du proxy */}
        <div style={{
          padding: '8px 10px', fontSize: 12, lineHeight: 1.45, borderRadius: 8,
          background: 'var(--color-background-secondary)', border: '1px solid var(--color-border)',
          color: 'var(--color-text-secondary)',
        }}>
          <strong style={{ color: 'var(--color-text)' }}>Promotion d'un bloc :</strong> dès que la procédure contient
          « Update images », le CICD arrête le proxy avant toute étape serveurs et le redémarre en dernier, quel que
          soit l'ordre ici. Les stop, kill, start et restart du proxy sont joués à ces deux moments, les proxys sont
          retirés des autres stop et start, et le modèle des lobbies de Nexus (eggs) passe sur la nouvelle image juste
          après l'arrêt. L'ordre exact s'affiche dans la fenêtre « Promouvoir vers Prod ».
        </div>

        {/* Sélecteur de procédure nommée */}
        {!loading && procNames.length > 0 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 10px',
            background: 'var(--color-background-secondary)',
            border: '1px solid var(--color-border)',
            borderRadius: 8,
            flexWrap: 'wrap',
          }}>
            <label style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--color-text-muted)', fontWeight: 600 }}>
              Procédure
            </label>
            <select
              value={currentProcName}
              onChange={(e) => setProcNameByEnv((prev) => ({ ...prev, [currentEnv]: e.target.value }))}
              disabled={saving}
              style={{
                padding: '4px 8px',
                fontSize: 13,
                borderRadius: 4,
                border: '1px solid var(--color-border)',
                background: 'var(--color-surface)',
                color: 'var(--color-text)',
                minWidth: 200,
              }}
            >
              {procNames.map((n) => (
                <option key={n} value={n}>{n} ({(procsForCurrentEnv[n]?.steps.length ?? 0)} étapes)</option>
              ))}
            </select>
            <div style={{ flex: 1 }} />
            <Button size="sm" variant="ghost" leftIcon={<Icon name="plus" size={11} />}
              onClick={createNewProcedure} disabled={saving}>
              Nouvelle
            </Button>
            <Button size="sm" variant="ghost" leftIcon={<Icon name="x" size={11} />}
              onClick={() => void deleteCurrent()}
              disabled={saving || procNames.length <= 1}
              title={procNames.length <= 1 ? "Impossible de supprimer la dernière procédure de l'env" : `Supprimer "${currentProcName}"`}>
              Supprimer
            </Button>
          </div>
        )}

        {error && <div style={{ padding: '10px 12px', background: 'var(--color-danger-bg)', color: 'var(--color-danger)', borderRadius: 8, fontSize: 13 }}>{error}</div>}
        {notice && <div style={{ padding: '10px 12px', background: 'var(--color-success-bg)', color: 'var(--color-success)', borderRadius: 8, fontSize: 13 }}>{notice}</div>}

        {loading ? (
          <div style={{ color: 'var(--color-text-muted)' }}>Chargement…</div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {current.steps.length === 0 && (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--color-text-muted)', border: '1px dashed var(--color-border)', borderRadius: 8 }}>
                  Aucune étape. Clique sur "Reset défaut Paladium" ou ajoute une étape ci-dessous.
                </div>
              )}
              {current.steps.map((s, idx) => (
                <ProcedureStepRow
                  key={idx}
                  idx={idx}
                  step={s}
                  total={current.steps.length}
                  onChange={(patch) => updateStep(idx, patch)}
                  onRemove={() => removeStep(idx)}
                  onMove={(dir) => moveStep(idx, dir)}
                />
              ))}
            </div>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', paddingTop: 12, borderTop: '1px solid var(--color-border)' }}>
              <span style={{ fontSize: 12, color: 'var(--color-text-muted)', alignSelf: 'center', marginRight: 4 }}>Ajouter :</span>
              <Button size="sm" variant="ghost" leftIcon={<Icon name="plus" size={11} />} onClick={() => addStep('command')}>Commande</Button>
              <Button size="sm" variant="ghost" leftIcon={<Icon name="plus" size={11} />} onClick={() => addStep('power')}>Power</Button>
              <Button size="sm" variant="ghost" leftIcon={<Icon name="plus" size={11} />} onClick={() => addStep('sleep')}>Sleep</Button>
              <Button size="sm" variant="ghost" leftIcon={<Icon name="plus" size={11} />} onClick={() => addStep('update-images')}>Update images</Button>
              <Button size="sm" variant="ghost" leftIcon={<Icon name="plus" size={11} />} onClick={() => addStep('update-apis')}>Update APIs</Button>
              <Button size="sm" variant="ghost" leftIcon={<Icon name="plus" size={11} />} onClick={() => addStep('cdn-promote')}>CDN promote</Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function ProcedureStepRow({ idx, step, total, onChange, onRemove, onMove }: {
  idx: number;
  step: import('@/deploy/services/procedure.service').ProcedureStep;
  total: number;
  onChange: (patch: Partial<import('@/deploy/services/procedure.service').ProcedureStep>) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
}) {
  const t = step.type;
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 6,
      padding: 10,
      background: 'var(--color-background-secondary)',
      border: '1px solid var(--color-border)',
      borderRadius: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 11, color: 'var(--color-text-muted)', minWidth: 24 }}>{idx + 1}.</span>
        <select value={t} onChange={(e) => onChange({ type: e.target.value as import('@/deploy/services/procedure.service').StepType })}
          style={{ padding: '4px 8px', fontSize: 13, borderRadius: 4, border: '1px solid var(--color-border)', background: 'var(--color-surface)' }}>
          <option value="command">Commande</option>
          <option value="power">Power signal</option>
          <option value="sleep">Sleep</option>
          <option value="update-images">Update images (containers MC)</option>
          <option value="update-apis">Update APIs (bump + restart)</option>
          <option value="cdn-promote">CDN promote</option>
        </select>
        <input type="text" placeholder="Label (optionnel)" value={step.label ?? ''}
          onChange={(e) => onChange({ label: e.target.value })}
          style={{ flex: 1, padding: '4px 8px', fontSize: 13, borderRadius: 4, border: '1px solid var(--color-border)', background: 'var(--color-surface)' }} />
        <button type="button" onClick={() => onMove(-1)} disabled={idx === 0}
          style={{ padding: '4px 8px', border: '1px solid var(--color-border)', background: 'var(--color-surface)', borderRadius: 4, cursor: idx === 0 ? 'not-allowed' : 'pointer', opacity: idx === 0 ? 0.4 : 1 }}>↑</button>
        <button type="button" onClick={() => onMove(1)} disabled={idx === total - 1}
          style={{ padding: '4px 8px', border: '1px solid var(--color-border)', background: 'var(--color-surface)', borderRadius: 4, cursor: idx === total - 1 ? 'not-allowed' : 'pointer', opacity: idx === total - 1 ? 0.4 : 1 }}>↓</button>
        <button type="button" onClick={onRemove}
          style={{ padding: '4px 8px', border: '1px solid var(--color-danger)', background: 'var(--color-danger-bg)', color: 'var(--color-danger)', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>✕</button>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingLeft: 32 }}>
        {(t === 'command' || t === 'power' || t === 'update-images' || t === 'update-apis') && (
          <label style={{ flex: 1, minWidth: 220, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 10, textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
              Filtre image docker <em style={{ fontStyle: 'normal', fontSize: 9 }}>(exact, prefix*, !exact, * = tous)</em>
            </span>
            <input type="text" placeholder="harbor.be.recube.gg/paladium-bedrock/proxy"
              value={step.imageFilter ?? ''} onChange={(e) => onChange({ imageFilter: e.target.value })}
              style={{ padding: '4px 8px', fontSize: 12, fontFamily: 'var(--font-family-mono)', borderRadius: 4, border: '1px solid var(--color-border)', background: 'var(--color-surface)' }} />
          </label>
        )}
        {t === 'command' && (
          <label style={{ flex: 1, minWidth: 220, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 10, textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
              Commande console <em style={{ fontStyle: 'normal', fontSize: 9 }}>(les " { } sont OK — Jackson échappe tout)</em>
            </span>
            <textarea
              placeholder={'whitelist on\nou : tellraw @a {"text":"Maintenance"}'}
              value={step.command ?? ''}
              onChange={(e) => onChange({ command: e.target.value })}
              rows={Math.min(4, Math.max(1, (step.command ?? '').split('\n').length))}
              spellCheck={false}
              style={{
                padding: '4px 8px',
                fontSize: 12,
                fontFamily: 'var(--font-family-mono)',
                borderRadius: 4,
                border: '1px solid var(--color-border)',
                background: 'var(--color-surface)',
                resize: 'vertical',
                minHeight: 24,
              }}
            />
          </label>
        )}
        {t === 'power' && (
          <label style={{ minWidth: 160, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 10, textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>Signal</span>
            <select value={step.powerSignal ?? 'stop'} onChange={(e) => onChange({ powerSignal: e.target.value as import('@/deploy/services/procedure.service').PowerSignal })}
              style={{ padding: '4px 8px', fontSize: 12, borderRadius: 4, border: '1px solid var(--color-border)', background: 'var(--color-surface)' }}>
              <option value="start">Start</option>
              <option value="stop">Stop (propre)</option>
              <option value="restart">Restart</option>
              <option value="kill">Kill (force)</option>
            </select>
          </label>
        )}
        {t === 'sleep' && (
          <label style={{ minWidth: 140, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 10, textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>Secondes</span>
            <input type="number" min={0} value={step.sleepSeconds ?? 5}
              onChange={(e) => onChange({ sleepSeconds: Number(e.target.value) || 0 })}
              style={{ padding: '4px 8px', fontSize: 12, fontFamily: 'var(--font-family-mono)', borderRadius: 4, border: '1px solid var(--color-border)', background: 'var(--color-surface)' }} />
          </label>
        )}
        {t === 'update-apis' && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 220, cursor: 'pointer' }}>
            <input type="checkbox"
              checked={step.restartOnlyIfUpdated !== false}
              onChange={(e) => onChange({ restartOnlyIfUpdated: e.target.checked })} />
            <span style={{ fontSize: 12 }}>
              Restart <strong>uniquement</strong> les APIs qui ont été updatées
              <br />
              <em style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>
                Décoche pour restart TOUTES les APIs matchant le filtre (utile pour reboot batch).
                Les API de base de données (MongoDB…) ne sont ni mises à jour ni redémarrées : à faire à la main.
              </em>
            </span>
          </label>
        )}
        {t === 'cdn-promote' && (
          <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 10, textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>Env source (info) <em style={{ fontStyle: 'normal', fontSize: 9 }}>l'upload va sur l'env cible avec sa propre config CDN</em></span>
            <input type="text" placeholder="preprod" value={step.sourceEnv ?? ''}
              onChange={(e) => onChange({ sourceEnv: e.target.value })}
              style={{ padding: '4px 8px', fontSize: 12, fontFamily: 'var(--font-family-mono)', borderRadius: 4, border: '1px solid var(--color-border)', background: 'var(--color-surface)' }} />
          </label>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Change password modal — auto-changement de mot de passe
// ============================================================
function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const validPassword = newPassword.length >= 8;
  const passwordsMatch = newPassword === confirmPassword;
  const valid = !!currentPassword && validPassword && passwordsMatch;

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const { usersService } = await import('@/deploy/services/users.service');
      await usersService.changeMyPassword({ currentPassword, newPassword });
      setSuccess(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur inconnue');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open onClose={onClose}
      title="Changer mon mot de passe"
      subtitle="Saisis ton mot de passe actuel + le nouveau (min 8 caractères)"
      width="sm"
      footer={success ? (
        <Button variant="primary" onClick={onClose}>Fermer</Button>
      ) : (
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Annuler</Button>
          <Button variant="primary" onClick={submit} disabled={!valid || submitting} loading={submitting}>
            Mettre à jour
          </Button>
        </>
      )}
    >
      <div className={styles.form}>
        {error && <div style={{ padding: '10px 12px', background: 'var(--color-danger-bg)', color: 'var(--color-danger)', borderRadius: 8, fontSize: 13 }}>{error}</div>}
        {success ? (
          <div style={{ padding: '12px', background: 'var(--color-success-bg)', color: 'var(--color-success)', borderRadius: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="check" size={14} /> Mot de passe mis à jour. Tu peux fermer.
          </div>
        ) : (
          <>
            <label className={styles.field}>
              <span>Mot de passe actuel</span>
              <input type="password" autoComplete="current-password"
                value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
            </label>
            <label className={styles.field}>
              <span>Nouveau mot de passe <em>min 8 caractères</em></span>
              <input type="password" autoComplete="new-password"
                value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
              {!!newPassword && !validPassword && (
                <span style={{ fontSize: 11, color: 'var(--color-danger)' }}>Trop court</span>
              )}
            </label>
            <label className={styles.field}>
              <span>Confirmer le nouveau mot de passe</span>
              <input type="password" autoComplete="new-password"
                value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
              {!!confirmPassword && !passwordsMatch && (
                <span style={{ fontSize: 11, color: 'var(--color-danger)' }}>Ne correspond pas</span>
              )}
            </label>
          </>
        )}
      </div>
    </Modal>
  );
}

// ============================================================
// Pterodactyl API config modal — 4 onglets (dev/beta/preprod/prod)
// Ptero dédié aux APIs, séparé du Ptero containers.
// Dev et Bêta partagent les serveurs API : l'onglet Bêta est en lecture seule (hérite de Dev).
// ============================================================
function PteroApiConfigModal({ projectId, projectName, onClose }: { projectId: string; projectName: string; onClose: () => void }) {
  const ENVS: { id: 'dev' | 'beta' | 'preprod' | 'prod'; label: string }[] = [
    { id: 'dev', label: 'Dev' },
    { id: 'beta', label: 'Beta' },
    { id: 'preprod', label: 'Pré-prod' },
    { id: 'prod', label: 'Prod' },
  ];
  type EnvId = (typeof ENVS)[number]['id'];
  const emptyCfg: import('@/deploy/services/pterodactyl-config.service').PterodactylConfig = {
    enabled: false, panelUrl: '', ptla: '', ptlc: '',
    dbHost: '', dbPort: 3306, dbName: '', dbUser: '', dbPassword: '',
  };

  const [configs, setConfigs] = useState<Record<EnvId, import('@/deploy/services/pterodactyl-config.service').PterodactylConfig>>({
    dev: emptyCfg, beta: emptyCfg, preprod: emptyCfg, prod: emptyCfg,
  });
  const [currentEnv, setCurrentEnv] = useState<EnvId>('preprod');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dbTesting, setDbTesting] = useState(false);
  const [dbTestResult, setDbTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const { apiPterodactylConfigService } = await import('@/deploy/services/api-pterodactyl-config.service');
        const all = await apiPterodactylConfigService.listAll(projectId);
        const next: Record<EnvId, import('@/deploy/services/pterodactyl-config.service').PterodactylConfig> = { ...configs };
        for (const e of ENVS) next[e.id] = { ...emptyCfg, ...(all[e.id] ?? {}) };
        setConfigs(next);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Erreur de chargement');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const updateCurrent = (patch: Partial<import('@/deploy/services/pterodactyl-config.service').PterodactylConfig>) => {
    setConfigs((prev) => ({ ...prev, [currentEnv]: { ...prev[currentEnv], ...patch } }));
  };

  const save = async () => {
    setError(null); setNotice(null); setSaving(true);
    try {
      const { apiPterodactylConfigService } = await import('@/deploy/services/api-pterodactyl-config.service');
      await apiPterodactylConfigService.update(projectId, currentEnv, configs[currentEnv]);
      setNotice(`Config Ptero API ${currentEnv} sauvegardée ✓`);
      setTimeout(() => setNotice(null), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur inconnue');
    } finally {
      setSaving(false);
    }
  };

  const testDb = async () => {
    setDbTesting(true);
    setDbTestResult(null);
    try {
      const { apiPterodactylConfigService } = await import('@/deploy/services/api-pterodactyl-config.service');
      const res = await apiPterodactylConfigService.testDb(projectId, currentEnv, configs[currentEnv]);
      setDbTestResult(res);
    } catch (e) {
      setDbTestResult({ ok: false, message: e instanceof Error ? e.message : 'Erreur' });
    } finally {
      setDbTesting(false);
    }
  };

  const cfg = configs[currentEnv];
  // Le CICD résout la config Ptero des APIs bêta sur celle de dev (mêmes serveurs) :
  // la config bêta n'est plus utilisée à l'exécution, on ne la propose donc plus à l'édition.
  const inherited = currentEnv === 'beta';

  return (
    <Modal
      open onClose={onClose}
      title="Configuration Pterodactyl APIs"
      subtitle={`${projectName} · Ptero dédié aux services API · Dev + Bêta, Pré-prod, Prod`}
      width="lg"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={saving}>Fermer</Button>
        <Button variant="primary" onClick={save} disabled={loading || saving || inherited} loading={saving}>
          Sauvegarder {currentEnv}
        </Button>
      </>}
    >
      <div className={styles.form}>
        <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--color-border)', marginBottom: 4 }}>
          {ENVS.map((e) => {
            const c = configs[e.id];
            return (
              <button key={e.id} type="button" onClick={() => setCurrentEnv(e.id)}
                style={{
                  padding: '8px 14px',
                  background: currentEnv === e.id ? 'var(--color-surface)' : 'transparent',
                  border: `1px solid ${currentEnv === e.id ? 'var(--color-border)' : 'transparent'}`,
                  borderBottom: currentEnv === e.id ? '1px solid var(--color-surface)' : 'none',
                  borderRadius: '6px 6px 0 0', marginBottom: -1, cursor: 'pointer',
                  fontSize: 14, fontWeight: 600,
                  color: currentEnv === e.id ? 'var(--color-text)' : 'var(--color-text-muted)',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}>
                {e.label}
                {e.id === 'beta'
                  ? <span style={{ fontSize: 10, fontWeight: 500, color: 'var(--color-text-muted)' }}>= Dev</span>
                  : c.enabled && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--color-success)' }} />}
              </button>
            );
          })}
        </div>

        {error && <div style={{ padding: '10px 12px', background: 'var(--color-danger-bg)', color: 'var(--color-danger)', borderRadius: 8, fontSize: 13 }}>{error}</div>}
        {notice && <div style={{ padding: '10px 12px', background: 'var(--color-success-bg)', color: 'var(--color-success)', borderRadius: 8, fontSize: 13 }}>{notice}</div>}

        {loading ? (
          <div style={{ color: 'var(--color-text-muted)' }}>Chargement…</div>
        ) : inherited ? (
          <div style={{
            padding: 12, display: 'flex', gap: 10, alignItems: 'flex-start',
            background: 'var(--color-background-secondary)', border: '1px dashed var(--color-border)',
            borderRadius: 8, fontSize: 13, color: 'var(--color-text-secondary)',
          }}>
            <Icon name="lock" size={14} />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <strong style={{ color: 'var(--color-text)' }}>Hérite de Dev pour les API</strong>
              <span>
                Dev et Bêta partagent les mêmes serveurs API : déploiement, Release, fichiers, console et
                contrôles de l'onglet Bêta utilisent la config Dev. Cette config Bêta n'est plus lue pour
                les API.
              </span>
              <div>
                <Button variant="secondary" size="sm" onClick={() => setCurrentEnv('dev')}>
                  Modifier la config Dev
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <Toggle
              checked={cfg.enabled}
              onChange={(v) => updateCurrent({ enabled: v })}
              label={`Ptero API activé pour ${currentEnv}`}
              description="Si désactivé, le deploy et le file explorer refuseront pour cet env."
            />

            <label className={styles.field}>
              <span>URL du panel</span>
              <input type="text" value={cfg.panelUrl ?? ''} onChange={(e) => updateCurrent({ panelUrl: e.target.value })}
                placeholder="https://ptero-apis.paladium.games" />
            </label>

            <label className={styles.field}>
              <span>PTLA — Application key <em>admin (listing servers + update)</em></span>
              <input type="password" autoComplete="new-password" value={cfg.ptla ?? ''}
                onChange={(e) => updateCurrent({ ptla: e.target.value })} placeholder="ptla_..." />
            </label>

            <label className={styles.field}>
              <span>PTLC — Client key <em>file explorer + commandes</em></span>
              <input type="password" autoComplete="new-password" value={cfg.ptlc ?? ''}
                onChange={(e) => updateCurrent({ ptlc: e.target.value })} placeholder="ptlc_..." />
            </label>

            <div style={{
              marginTop: 12, padding: 12,
              background: 'var(--color-background-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 8,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Icon name="settings" size={14} />
                <strong style={{ fontSize: 13 }}>Connexion MySQL Ptero <em style={{ fontStyle: 'normal', fontSize: 11, opacity: 0.7, fontWeight: 400 }}>(requise pour update eggs)</em></strong>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <label className={styles.field} style={{ flex: 3 }}>
                  <span>Host MySQL</span>
                  <input type="text" value={cfg.dbHost ?? ''} onChange={(e) => updateCurrent({ dbHost: e.target.value })}
                    placeholder="mysql.ptero-apis.internal" />
                </label>
                <label className={styles.field} style={{ flex: 1 }}>
                  <span>Port</span>
                  <input type="number" value={cfg.dbPort ?? 3306} onChange={(e) => updateCurrent({ dbPort: Number(e.target.value) || 3306 })} />
                </label>
              </div>

              <label className={styles.field}>
                <span>Database</span>
                <input type="text" value={cfg.dbName ?? ''} onChange={(e) => updateCurrent({ dbName: e.target.value })}
                  placeholder="pterodactyl" />
              </label>

              <div style={{ display: 'flex', gap: 8 }}>
                <label className={styles.field} style={{ flex: 1 }}>
                  <span>User</span>
                  <input type="text" autoComplete="new-password" value={cfg.dbUser ?? ''}
                    onChange={(e) => updateCurrent({ dbUser: e.target.value })} placeholder="recube_eggs" />
                </label>
                <label className={styles.field} style={{ flex: 1 }}>
                  <span>Password</span>
                  <input type="password" autoComplete="new-password" value={cfg.dbPassword ?? ''}
                    onChange={(e) => updateCurrent({ dbPassword: e.target.value })} placeholder="••••••••" />
                </label>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Button variant="secondary" size="sm" onClick={() => void testDb()}
                  disabled={dbTesting || !cfg.dbHost || !cfg.dbName || !cfg.dbUser || !cfg.dbPassword}
                  loading={dbTesting}>
                  Tester la connexion
                </Button>
                {dbTestResult && (
                  <span style={{
                    fontSize: 12,
                    color: dbTestResult.ok ? 'var(--color-success)' : 'var(--color-danger)',
                  }}>
                    <Icon name={dbTestResult.ok ? 'check' : 'warn'} size={11} /> {dbTestResult.message}
                  </span>
                )}
              </div>
            </div>

            {/* Fresh deploy API — visible sur les 4 onglets (chaque env a son propre
                Ptero API et donc sa propre config egg/owner). */}
            <FreshApiDeploySection cfg={cfg} updateCurrent={updateCurrent} envLabel={currentEnv} />
          </>
        )}
      </div>
    </Modal>
  );
}

/**
 * Config Ptero "fresh deploy API" — visible sur les 4 onglets.
 */
function FreshApiDeploySection({
  cfg, updateCurrent, envLabel,
}: {
  cfg: import('@/deploy/services/pterodactyl-config.service').PterodactylConfig;
  updateCurrent: (patch: Partial<import('@/deploy/services/pterodactyl-config.service').PterodactylConfig>) => void;
  envLabel: string;
}) {
  return (
    <div style={{
      marginTop: 12, padding: 12,
      background: 'var(--color-background-secondary)',
      border: '1px solid var(--color-border)',
      borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon name="rocket" size={14} />
        <strong style={{ fontSize: 13 }}>
          Fresh deploy API ({envLabel})
          <em style={{ fontStyle: 'normal', fontSize: 11, opacity: 0.7, fontWeight: 400 }}>
            &nbsp;— création serveur Ptero à la volée quand une API n'a pas encore de serveur ici
          </em>
        </strong>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <label className={styles.field} style={{ flex: 1 }}>
          <span>Egg ID API</span>
          <input type="number" value={cfg.apiCreateEggId ?? ''}
            onChange={(e) => updateCurrent({ apiCreateEggId: e.target.value ? Number(e.target.value) : null })} />
        </label>
        <label className={styles.field} style={{ flex: 1 }}>
          <span>Node ID <em>optionnel, auto-alloc si vide</em></span>
          <input type="number" value={cfg.apiCreateNodeId ?? ''}
            onChange={(e) => updateCurrent({ apiCreateNodeId: e.target.value ? Number(e.target.value) : null })} />
        </label>
        <label className={styles.field} style={{ flex: 1 }}>
          <span>Owner User ID</span>
          <input type="number" value={cfg.defaultUserId ?? ''}
            onChange={(e) => updateCurrent({ defaultUserId: e.target.value ? Number(e.target.value) : null })} />
        </label>
      </div>
    </div>
  );
}

/**
 * Champs supplémentaires visibles uniquement pour l'onglet Ptero API "dev".
 * Utilisés par la features Dev envs (création serveur) et Beta slots (promote NFS).
 */
function DevExtrasSection({
  cfg, updateCurrent,
}: {
  cfg: import('@/deploy/services/pterodactyl-config.service').PterodactylConfig;
  updateCurrent: (patch: Partial<import('@/deploy/services/pterodactyl-config.service').PterodactylConfig>) => void;
}) {
  const slots = ['A', 'B', 'C', 'D', 'E'];
  const uuids = cfg.betaSlotUuids ?? {};
  const addresses = cfg.betaSlotAddresses ?? {};
  const setSlot = (slot: string, value: string) => {
    const next = { ...(cfg.betaSlotUuids ?? {}) };
    if (value.trim()) next[slot] = value.trim();
    else delete next[slot];
    updateCurrent({ betaSlotUuids: next });
  };
  const setAddress = (slot: string, value: string) => {
    const next = { ...(cfg.betaSlotAddresses ?? {}) };
    if (value.trim()) next[slot] = value.trim();
    else delete next[slot];
    updateCurrent({ betaSlotAddresses: next });
  };
  return (
    <div style={{
      marginTop: 12, padding: 12,
      background: 'var(--color-background-secondary)',
      border: '1px solid var(--color-border)',
      borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon name="server" size={14} />
        <strong style={{ fontSize: 13 }}>
          Extras Dev / Beta <em style={{ fontStyle: 'normal', fontSize: 11, opacity: 0.7, fontWeight: 400 }}>(création serveurs + clone NFS)</em>
        </strong>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <label className={styles.field} style={{ flex: 1 }}>
          <span>Egg ID (Ptero)</span>
          <input type="number" value={cfg.defaultEggId ?? ''}
            onChange={(e) => updateCurrent({ defaultEggId: e.target.value ? Number(e.target.value) : null })} />
        </label>
        <label className={styles.field} style={{ flex: 1 }}>
          <span>Node ID</span>
          <input type="number" value={cfg.defaultNodeId ?? ''}
            onChange={(e) => updateCurrent({ defaultNodeId: e.target.value ? Number(e.target.value) : null })} />
        </label>
        <label className={styles.field} style={{ flex: 1 }}>
          <span>Owner User ID</span>
          <input type="number" value={cfg.defaultUserId ?? ''}
            onChange={(e) => updateCurrent({ defaultUserId: e.target.value ? Number(e.target.value) : null })} />
        </label>
      </div>
      <label className={styles.field}>
        <span>Chemin NFS local vers <code>/var/lib/pterodactyl/volumes</code></span>
        <input type="text" value={cfg.nfsVolumesPath ?? ''}
          onChange={(e) => updateCurrent({ nfsVolumesPath: e.target.value })}
          placeholder="/mnt/nfs/ptero-dev/volumes" />
      </label>

      <div>
        <div style={{ fontSize: 12, marginBottom: 6, color: 'var(--color-text-muted)' }}>
          Slots beta pré-créés — UUID Ptero (interne) + adresse publique visible côté client
          <em style={{ fontStyle: 'normal', display: 'block', opacity: 0.7, marginTop: 2 }}>
            L'adresse publique est utile quand les serveurs sont derrière un reverse proxy (l'IP Ptero ≠ IP client).
          </em>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {slots.map((s) => (
            <div key={s} style={{ display: 'grid', gridTemplateColumns: '32px 1fr 1fr', gap: 6, alignItems: 'center' }}>
              <strong style={{ fontSize: 13, textAlign: 'center' }}>{s}</strong>
              <input type="text" value={uuids[s] ?? ''}
                onChange={(e) => setSlot(s, e.target.value)}
                placeholder="uuid Ptero"
                style={{ padding: '4px 8px', fontSize: 12, fontFamily: 'var(--font-family-mono)',
                  borderRadius: 4, border: '1px solid var(--color-border)', background: 'var(--color-surface)' }} />
              <input type="text" value={addresses[s] ?? ''}
                onChange={(e) => setAddress(s, e.target.value)}
                placeholder="beta-a.recube.gg:25567"
                style={{ padding: '4px 8px', fontSize: 12, fontFamily: 'var(--font-family-mono)',
                  borderRadius: 4, border: '1px solid var(--color-border)', background: 'var(--color-surface)' }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
